import { Editor, Element as SlateElement, Transforms } from "slate";
import { ReactEditor } from "slate-react";

import { restrictions } from "./restrictions";
import { CustomTextKey, CustomEditor, CustomElement } from "./types";

export const isValidUrl = (url: string): boolean => {
    try {
        new URL(url);
        return true;
    } catch {
        return false;
    }
};

export const isAvailable = (editor: CustomEditor, format?: string): boolean => {
    if (!format || !editor.selection) {
        return true;
    }

    const [nodeEntry] = Array.from(
        Editor.nodes(editor, {
            at: editor.selection,
            match: n => !Editor.isEditor(n) && SlateElement.isElement(n),
        }),
    );

    const contextType = nodeEntry && SlateElement.isElement(nodeEntry[0]) ? nodeEntry[0].type : "";
    return !restrictions[contextType]?.includes(format);
};

export const isMarkdown = (text: string): boolean => {
    const markdownPatterns = [
        /^#{1,6}\s+/m, // Headers
        /\*\*.*?\*\*/, // Bold
        /(?<!\*)\*(?!\*).*?\*(?!\*)/, // Italic (single asterisk, not double)
        /_.*?_/, // Italic (underscore)
        /`.*?`/, // Inline code
        /```[\s\S]*?```/, // Code blocks
        /^\s*[-*+]\s+/m, // Lists
        /^\s*\d+\.\s+/m, // Numbered lists
        /^\s*>\s+/m, // Quotes
        /!\[.*?\]\(.*?\)/, // Images
        /\[.*?\]\(.*?\)/, // Links
        /~~.*?~~/, // Strikethrough
        /<u>.*?<\/u>/, // Underline
    ];

    return markdownPatterns.some(pattern => pattern.test(text));
};

export const isBlockActive = (editor: CustomEditor, format: CustomElement["type"]): boolean => {
    if (!editor.selection) {
        return false;
    }

    if (format === "code") {
        return !!Editor.above(editor, { match: n => SlateElement.isElement(n) && n.type === "code" });
    }

    const [match] = Array.from(
        Editor.nodes(editor, {
            at: Editor.unhangRange(editor, editor.selection),
            match: n => SlateElement.isElement(n) && n.type === format,
        }),
    );
    return !!match;
};

export const isMarkActive = (editor: CustomEditor, format: CustomTextKey): boolean => {
    const marks = Editor.marks(editor);
    return format === "link" ? !!marks?.link : marks?.[format] === true;
};

export const toggleBlock = (editor: CustomEditor, format: CustomElement["type"]) => {
    const isActive = isBlockActive(editor, format);
    const elementMatch = { match: (n: any) => !Editor.isEditor(n) && SlateElement.isElement(n) };

    if (format === "numbered-list" || format === "bulleted-list") {
        Transforms.unwrapNodes(editor, {
            ...elementMatch,
            match: (n: any) => SlateElement.isElement(n) && (n.type === "numbered-list" || n.type === "bulleted-list"),
            split: true,
        });
        if (!isActive) {
            Transforms.setNodes(editor, { type: "list-item" } as Partial<CustomElement>, elementMatch);
            Transforms.wrapNodes(editor, { type: format, children: [] });
        } else {
            // Convertir les list-item en paragraphs lors de la désactivation
            Transforms.setNodes(editor, { type: "paragraph" } as Partial<CustomElement>, {
                ...elementMatch,
                match: (n: any) => SlateElement.isElement(n) && n.type === "list-item",
            });
        }
    } else if (format === "code") {
        if (isActive) {
            Transforms.unwrapNodes(editor, { match: (n: any) => SlateElement.isElement(n) && n.type === "code" });
        } else {
            // Préserver le contenu existant lors de la transformation en bloc code
            if (!editor.selection) {
                return;
            }

            // Obtenir tous les nœuds dans la sélection
            const nodes = Array.from(
                Editor.nodes(editor, {
                    at: editor.selection,
                    match: n => SlateElement.isElement(n) && n.type !== "code",
                }),
            );

            if (nodes.length > 0) {
                // Extraire le texte en préservant les retours à la ligne
                const textParts: string[] = [];
                for (const [node] of nodes) {
                    if (SlateElement.isElement(node)) {
                        const nodeText = Editor.string(editor, ReactEditor.findPath(editor, node));
                        if (nodeText.trim()) {
                            textParts.push(nodeText);
                        }
                    }
                }
                const selectedText = textParts.join("\n");

                // Supprimer les nœuds existants dans la sélection
                Transforms.delete(editor, { at: editor.selection });

                // Insérer un nouveau bloc code avec le contenu préservé
                Transforms.insertNodes(editor, {
                    type: "code",
                    language: "text",
                    children: selectedText ? [{ text: selectedText }] : [{ text: "" }],
                } as CustomElement);
            } else {
                // Fallback: créer un bloc code vide
                Transforms.wrapNodes(editor, { type: "code", language: "text", children: [] });
            }
        }
    } else if (format === "image") {
        const url = prompt("Entrez l'URL de l'image:");
        if (url) {
            if (!isValidUrl(url)) {
                alert("URL invalide");
                return;
            }
            Transforms.insertNodes(editor, [
                { type: "image", url, children: [{ text: "" }] },
                { type: "paragraph", children: [{ text: "" }] },
            ]);
        }
    } else {
        Transforms.setNodes(editor, { type: isActive ? "paragraph" : format } as Partial<CustomElement>, elementMatch);
    }
};

export const toggleMark = (editor: CustomEditor, format: CustomTextKey) => {
    const isActive = isMarkActive(editor, format);

    if (format === "link") {
        if (isActive) {
            Editor.removeMark(editor, "link");
        } else {
            const url = prompt("Entrez l'URL du lien:");
            if (url) {
                if (!isValidUrl(url)) {
                    alert("URL invalide");
                    return;
                }
                Editor.addMark(editor, "link", url);
            }
        }
    } else {
        if (isActive) {
            Editor.removeMark(editor, format);
        } else {
            Editor.addMark(editor, format, true);
        }
    }
};
